import { memo, useCallback } from 'react';
import { Divider, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { Accordion } from '@common/components/Accordion';
import { useReadSelectedAreaDetails } from '@/modules/floorplanDesign/hooks/useReadSelectedAreaDetails';
import { UserPreferenceName } from '@/modules/userPreferences/constants';
import { useUpdateUserPreferences, useUserPreference } from '@/modules/userPreferences/hooks';

import { AreaStationSummaryCard } from './AreaStationSummaryCard';

function AreaDetailsComponent() {
  const { t } = useTranslation('interface');
  const { combinedStationCount, selectedAreaCount, areaDetails } = useReadSelectedAreaDetails();
  const isAreaDetailsExpanded = useUserPreference(
    UserPreferenceName.PROPERTY_PANEL_AREA_DETAILS_EXPANDED,
  );
  const { updateUserPreference } = useUpdateUserPreferences();
  const hasMultipleSelectedAreas = selectedAreaCount > 1;

  const onAccordionChange = useCallback(
    (expanded: boolean) => {
      updateUserPreference(UserPreferenceName.PROPERTY_PANEL_AREA_DETAILS_EXPANDED, expanded);
    },
    [updateUserPreference],
  );

  if (areaDetails.length === 0) return null;

  return (
    <>
      <Divider sx={{ pt: 1 }} />
      <Accordion
        title={`${t('interface:properties.area.details', 'Area details')} (${selectedAreaCount})`}
        expanded={isAreaDetailsExpanded}
        onChange={onAccordionChange}
        unmountOnExit
        sx={{ pt: 1, px: 2 }}
      >
        <Stack spacing={1} sx={{ pb: 1 }}>
          {hasMultipleSelectedAreas && (
            <Stack
              direction='row'
              alignItems='baseline'
              justifyContent='space-between'
              spacing={2}
              sx={{ pb: 1 }}
            >
              <Typography variant='body2' color='text.secondary'>
                Total stations
              </Typography>
              <Typography
                variant='body1'
                fontWeight={600}
                sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
              >
                {combinedStationCount}
              </Typography>
            </Stack>
          )}

          {areaDetails.map((areaDetail) => (
            <Stack key={areaDetail.id} spacing={1} sx={{ pb: 1 }}>
              {areaDetail.summaries.map((summary) => (
                <AreaStationSummaryCard
                  key={summary.id}
                  summary={summary}
                  color={areaDetail.color}
                />
              ))}
            </Stack>
          ))}
        </Stack>
      </Accordion>
    </>
  );
}

export const AreaDetails = memo(AreaDetailsComponent);
